import React, { useState, useEffect } from "react";

import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  List,
  ListItem,
  ListItemText,
  TextField,
  CircularProgress,
} from "@material-ui/core";

import api from "../../services/api";
import { i18n } from "../../translate/i18n";
import toastError from "../../errors/toastError";
import useDebouncedValue from "../../hooks/useDebouncedValue";

// feat_299 — perfiles no-admin elegibles para "Ver como usuario".
// Espeja el guard del middleware backend (viewAsUser.ts): admin/superadmin
// quedan afuera. El backend valida igual — esto es solo la UI.
const ELIGIBLE_PROFILES = ["user", "profesional", "agendador"];
const MAX_PAGES = 10;

const ViewAsUserModal = ({ open, onClose, onSelect }) => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const debouncedSearch = useDebouncedValue(searchTerm, 300);

  useEffect(() => {
    if (!open) return undefined;

    let cancelled = false;

    const fetchUsers = async () => {
      setLoading(true);
      try {
        // /users pagina de a 20 → juntamos las páginas (cap defensivo) para que
        // el selector no esconda usuarios en empresas grandes.
        const collected = [];
        let pageNumber = 1;
        let hasMore = true;
        while (hasMore && pageNumber <= MAX_PAGES) {
          // eslint-disable-next-line no-await-in-loop
          const { data } = await api.get("/users", {
            params: { pageNumber, searchParam: debouncedSearch || undefined },
          });
          collected.push(...(data.users || []));
          hasMore = !!data.hasMore;
          pageNumber += 1;
        }
        if (cancelled) return;
        setUsers(
          collected.filter(
            u => ELIGIBLE_PROFILES.includes(u.profile) && !u.isSuperAdmin
          )
        );
      } catch (err) {
        if (!cancelled) toastError(err);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    fetchUsers();
    return () => {
      cancelled = true;
    };
  }, [open, debouncedSearch]);

  const handleClose = () => {
    setSearchTerm("");
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>{i18n.t("viewAs.modalTitle")}</DialogTitle>
      <DialogContent dividers>
        <TextField
          fullWidth
          autoFocus
          margin="dense"
          variant="outlined"
          placeholder={i18n.t("viewAs.searchPlaceholder")}
          value={searchTerm}
          onChange={e => setSearchTerm(e.target.value)}
        />
        {loading ? (
          <div style={{ display: "flex", justifyContent: "center", padding: 24 }}>
            <CircularProgress size={28} />
          </div>
        ) : (
          <List>
            {users.map(u => (
              <ListItem
                button
                key={u.id}
                onClick={() => {
                  onSelect(u.id);
                  handleClose();
                }}
              >
                <ListItemText
                  primary={u.name}
                  secondary={`${u.profile} · ${u.email}`}
                />
              </ListItem>
            ))}
            {users.length === 0 && (
              <ListItem>
                <ListItemText secondary={i18n.t("viewAs.noUsers")} />
              </ListItem>
            )}
          </List>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} color="secondary">
          {i18n.t("viewAs.cancel")}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ViewAsUserModal;
